import { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data.notifications);
      setUnread(res.data.unread_count);
    } catch (err) {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    await axios.put(`${API}/notifications/${id}/read`);
    fetchNotifications();
  };

  const markAllRead = async () => {
    await axios.put(`${API}/notifications/read-all`);
    fetchNotifications();
  };

  const typeColors = {
    info: 'bg-cyan/20 text-cyan',
    warning: 'bg-yellow-400/20 text-yellow-400',
    success: 'bg-green-400/20 text-green-400',
    update: 'bg-purple-400/20 text-purple-400',
    admin_message: 'bg-red-400/20 text-red-400',
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-slate-400 hover:text-white transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed top-14 right-4 md:absolute md:top-12 md:left-0 md:right-auto w-80 bg-navy-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-[100]" data-testid="notification-panel">
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <span className="text-white font-medium text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-cyan hover:text-cyan-300 flex items-center gap-1">
                <CheckCheck size={12} /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-slate-500 text-sm">No notifications yet</div>
            ) : notifications.map(n => (
              <div
                key={n.id}
                className={`p-3 border-b border-white/5 hover:bg-white/[0.02] ${!n.read ? 'bg-cyan/[0.03]' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full mt-0.5 flex-shrink-0 ${typeColors[n.type] || typeColors.info}`}>
                    {n.type}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{n.title}</p>
                    <p className="text-slate-400 text-xs mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-slate-600 text-[10px] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} className="text-slate-500 hover:text-cyan flex-shrink-0">
                      <Check size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
